import {useContext, useRef, useState} from 'react'

import PropTypes from 'prop-types'

import {ATOM_ICON_COLORS, ATOM_ICON_SIZES} from '@s-ui/react-atom-icon'
import Context from '@s-ui/react-context'
import {useToggle} from '@s-ui/react-hooks'
import useEventListener from '@s-ui/react-hooks/lib/useEventListener'
import MoleculeDrawer, {
  MoleculeDrawerOverlay,
  moleculeDrawerPlacements,
  moleculeDrawerSizes
} from '@s-ui/react-molecule-drawer'
import {useRouter} from '@s-ui/react-router'
import CardVehicle from '@adv-mt/mt-card-vehicle'
import Layout from '@adv-mt/mt-layout-basic'
import StepperFlow from '@adv-mt/mt-stepper-flow'
import IconClose from '@adv-mt/mt-svg-icons/lib/Close'
import TitleBasic, {titleBasicSizes, titleBasicTags} from '@adv-mt/mt-title-basic'
import TrackingPageView from '@adv-mt/mt-tracking-page-view'

import LinkFactory from '../../../components/LinkFactory/index.js'
import {withThirdPartiesParam} from '../../../hocs/withThirdPartiesParam.js'
import {ExitButton} from '../components/Buttons/exit-button.js'
import {Title} from '../components/Title/index.js'
import {useSuggesterTracking} from '../hooks/useSuggesterTracking.js'
import {defaultTopbarProps} from '../settings.js'
import {useVehicleOverviewInfo} from '../VehicleOverview/hooks/useVehicleOverviewInfo.js'
import {VehicleOverview} from '../VehicleOverview/index.js'
import {getInitialProps} from './getInitialProps.js'
import Skeleton from './Skeleton.js'

const VehicleSuggesterResults = ({
  h1,
  isError,
  pageName,
  suggestions = [],
  loadThirdParties,
  title,
  description,
  image
}) => {
  const {domain} = useContext(Context)
  const config = domain.get('config')
  const {HOME_PATH_PATTERN, VEHICLE_CONFIGURATOR_TRIM_LEVEL_PATH, VEHICLE_SUGGESTER_DETAILS_PAGE_NAME} =
    config.get('page')
  const {VEHICLE_SUGGESTER_TRACKING_ACTIONS: actions} = config.get('tracking')
  const drawerRef = useRef()
  const {trackAction, trackPage} = useSuggesterTracking()
  const [toggleDrawer, setToggleDrawer] = useToggle(false)
  const [selectedVehicleSpecs, setSelectedVehicleSpecs] = useState(null)
  const {info: vehicleInfo, loading} = useVehicleOverviewInfo({specs: selectedVehicleSpecs})
  const router = useRouter()

  const urlStatics = config.get('urlStatics')
  const DEFAULT_PHOTO = config.get('DEFAULT_IMAGE_PLACEHOLDER')
  const defaultImage = `${urlStatics}${DEFAULT_PHOTO}`

  useEventListener('beforeunload', event => {
    event.preventDefault()
    event.returnValue = ''
  })

  const topbarProps = {
    ...defaultTopbarProps,
    navButton: <ExitButton onClick={() => router.push(HOME_PATH_PATTERN)} />
  }

  if (isError) {
    return (
      <main>
        <Layout.FullWidth linkFactory={LinkFactory} footerProps={{isSimple: true}} topbarProps={topbarProps}>
          <Layout.Container isFullWidth isFullHeight>
            <StepperFlow.Error
              title={title}
              description={description}
              image={image}
              onBack={() => router.goBack()}
              onExit={() => router.push(HOME_PATH_PATTERN)}
            />
          </Layout.Container>
        </Layout.FullWidth>
      </main>
    )
  }

  const handleDetailClick = ({label, specs}) => {
    setSelectedVehicleSpecs(specs)
    setToggleDrawer(true)
    trackPage(VEHICLE_SUGGESTER_DETAILS_PAGE_NAME)
    trackAction({
      pageName,
      action: actions.DETAIL_CLICK,
      selectedOption: label
    })
  }

  const handleConfigureClick = async ({label, specs}) => {
    await trackAction({
      pageName,
      action: actions.CONFIGURE_CLICK,
      selectedOption: label
    })
    router.push({
      pathname: VEHICLE_CONFIGURATOR_TRIM_LEVEL_PATH,
      query: {
        makeId: specs.make?.id,
        modelId: specs.model?.id,
        fuelTypeId: specs.fuelType?.id
      }
    })
  }

  const handleCloseDrawer = () => {
    setSelectedVehicleSpecs(null)
    setToggleDrawer(false)
  }

  return (
    <main className="mt-VehicleSuggesterResults">
      <Layout.FullWidth linkFactory={LinkFactory} footerProps={{isSimple: true}} topbarProps={topbarProps}>
        <Layout.Container isFullWidth isFullHeight>
          <Title title={h1} />
          <div className="mt-VehicleSuggesterResults-container">
            {suggestions.map(({label, vehicles}, index) => {
              return (
                <section key={`section-${index}`} className="mt-VehicleSuggesterResults-suggestions">
                  <TitleBasic as={titleBasicTags.h2} size={titleBasicSizes.xs} title={label} />
                  <ul className="mt-VehicleSuggesterResults-suggestionsList">
                    {vehicles.map(specs => {
                      const {make, model, fuelType, hp, images} = specs
                      return (
                        <li key={`vehicle-${make?.id}-${model?.id}`}>
                          <CardVehicle
                            title={`${make?.name} ${model?.name}`}
                            fuelTypeId={fuelType?.id}
                            power={hp?.name}
                            image={images?.length ? images[0] : defaultImage}
                            onDetailClick={() => handleDetailClick({label, specs})}
                            onConfigureClick={() => handleConfigureClick({label, specs})}
                          />
                        </li>
                      )
                    })}
                  </ul>
                </section>
              )
            })}
            {loadThirdParties && <TrackingPageView pageName={pageName} />}
          </div>
        </Layout.Container>
      </Layout.FullWidth>

      <MoleculeDrawerOverlay isVisible={toggleDrawer} />
      <MoleculeDrawer
        closeOnOutsideClick
        isOpen={toggleDrawer}
        isPageScrollable={false}
        onClose={handleCloseDrawer}
        placement={moleculeDrawerPlacements.RIGHT}
        ref={drawerRef}
        size={moleculeDrawerSizes.M}
      >
        <div className="mt-VehicleSuggesterResults-drawer">
          <div className="mt-VehicleSuggesterResults-drawerClose">
            <IconClose color={ATOM_ICON_COLORS.primary} size={ATOM_ICON_SIZES.medium} onClick={handleCloseDrawer} />
          </div>
          <div className="mt-VehicleSuggesterResults-drawerContent">
            {selectedVehicleSpecs ? (
              <VehicleOverview
                info={vehicleInfo}
                loading={loading}
                onCtaClick={() => handleConfigureClick({label: 'view details', specs: selectedVehicleSpecs})}
              />
            ) : (
              <p>No hay vehículo seleccionado</p>
            )}
          </div>
        </div>
      </MoleculeDrawer>
    </main>
  )
}

VehicleSuggesterResults.displayName = 'VehicleSuggesterResults'
VehicleSuggesterResults.getInitialProps = getInitialProps

VehicleSuggesterResults.propTypes = {
  h1: PropTypes.string.isRequired,
  isError: PropTypes.bool,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  image: PropTypes.string.isRequired,
  pageName: PropTypes.string.isRequired,
  suggestions: PropTypes.arrayOf(PropTypes.object).isRequired,
  loadThirdParties: PropTypes.bool
}

VehicleSuggesterResults.renderLoading = () => <Skeleton />

export default withThirdPartiesParam(VehicleSuggesterResults)
